import React, { useState, useEffect, useMemo } from 'react';
import styles from './RightSidebarMenu.module.scss';
import RightSidebarHeader from './RightSidebarHeader';
import { useLibraryContext } from '../../context/LibraryContext';
import useFetchFoldersList from '../../hooks/useFetchFoldersList';
import { useTranslation } from 'react-i18next';
import { FOLDER_USER_ROLE } from '../../constants/UserRoles';
import { useUserContext } from '../../context/UserContext';
import { approvalStatuses } from '../../features/Dashboard/components/VideosFilter/constants';
import { ToastNotification } from '../Toast/Toast';
import { LoadingSpinner } from '../Icon/icons';
import SidebarButton from '../../routes/newVideo/VideoSidebarMenu/SidebarButton/SidebarButton';

const RightSidebarMenu = ({ folderId, userId, isArchive = false, shareFolder }) => {
  const {
    toggleCopyLinkToClipboard,
    toggleShareShow,
    selectedCard,
    toggleDownloadSelectedVideos,
    toggleDeleteVideos,
    toggleArchiveVideos,
    toggleRenameVideo,
    toggleDuplicateVideo,
    toggleUnarchive,
    selectedFolder,
    toggleRenameFolder,
    toggleDeleteFolders,
    toggleArchiveFolders,
    toggleUnarchiveFolders,
    toggleMoveVideos,
    toggleRemoveFromReviewVideo,
  } = useLibraryContext();

  const { list: folders, loading } = useFetchFoldersList({ archived: isArchive });

  const folder = useMemo(() => folders.find(({ id }) => id === folderId), [folders]);

  const isDisabled = Object.keys(selectedCard).length > 1 || Object.keys(selectedFolder).length > 1;

  const isFolderEditable = !!folder?.editingEnabled;
  const { isApprovalFlowEnabled } = useUserContext();
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  /* LOGIC PART FOR SIBEBAR IN FOLDERS

  1) Owner and editor always can edit all videos in folder
  2) Video owner and editor with advanced editing rights can move videos
  3) Viewer can edit only own videos in folder (but not another users videos)
  4) Folder Owner always can edit folder
  5) Editor can edit folder only if edit rights in folder enabled
  6) Team folder couldn't be archived

  All variables below are only used when the sidebar will be displayed in Folders
  */

  const isFolderOwner = useMemo(() => folder?.owner?.username === userId, [folder]);
  const isEditor = useMemo(
    () => folder?.users?.find(({ username }) => username === userId)?.folderRole === FOLDER_USER_ROLE.EDITOR.TYPE ?? false,
    [folder]
  );
  const isVideosOwner = useMemo(
    () => !!Object.values(selectedCard).length && Object.values(selectedCard).every(({ author }) => author.username === userId),
    [selectedCard]
  );

  const isUserHasEditingRights = useMemo(() => isVideosOwner || isFolderOwner || isEditor, [isVideosOwner, isFolderOwner, isEditor]);
  const isUserHasAdvancedEditingRights = useMemo(() => isVideosOwner || isFolderOwner || (isEditor && isFolderEditable), [
    isVideosOwner,
    isFolderOwner,
    isEditor,
    isFolderEditable,
  ]);

  const isTeamFolder =
    !!Object.values(selectedFolder).length &&
    Object.values(selectedFolder).every(({ users }) => {
      return users?.length > 0;
    });

  useEffect(() => {
    console.log('isUserHasEditingRights', isUserHasEditingRights);
  }, [isUserHasEditingRights]);

  /* LOGIC FOR SIDEBAR IN DASHBOARD

  1) Only folder owner can archive folder
  */
  const isFoldersOwner =
    !!Object.values(selectedFolder).length && Object.values(selectedFolder).every(({ owner }) => owner?.username === userId);
  const isFoldersAdmin =
    !!Object.values(selectedFolder).length &&
    Object.values(selectedFolder).every(({ users, editingEnabled }) => {
      return (
        users?.find(({ username, folderRole }) => username === userId && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE) &&
        !!editingEnabled
      );
    });

  const isPreExistedStatus = useMemo(
    () => Object.values(selectedCard).some(video => video?.approvalStatus === approvalStatuses.PRE_EXISTED.TYPE),
    [selectedCard]
  );
  const isInReviewStatus = useMemo(
    () => Object.values(selectedCard).some(video => video?.approvalStatus === approvalStatuses.IN_REVIEW.TYPE),
    [selectedCard]
  );
  const isApprovedStatus = useMemo(
    () => Object.values(selectedCard).some(video => video?.approvalStatus === approvalStatuses.APPROVED.TYPE),
    [selectedCard]
  );
  const isDraftVideos = useMemo(() => Object.values(selectedCard).some(video => video?.draft), [selectedCard]);

  const isProcessing = useMemo(() => Object.values(selectedCard).some(video => video?.thumbnail === null && !video?.draft), [
    selectedCard,
  ]);

  const { t } = useTranslation();

  const handleCopyLink = () => {
    toggleCopyLinkToClipboard();
    ToastNotification({
      message: t('general.toast.link_copied'),
      iconName: 'success',
      isMobileView: isMobileView,
    });
  };

  const handleShareClick = () => {
    toggleShareShow();
  };

  const handleMoveClick = () => {
    toggleMoveVideos();
  };

  const handleDownloadClick = () => {
    toggleDownloadSelectedVideos();
  };

  const handleDeleteClick = () => {
    Object.keys(selectedCard).length > 0 && toggleDeleteVideos();
    Object.keys(selectedFolder).length > 0 && toggleDeleteFolders();
  };

  const handleRenameClick = () => {
    toggleRenameVideo();
  };

  const handleDuplicateClick = () => {
    toggleDuplicateVideo();
  };
  const handleRenameFolderClick = () => {
    toggleRenameFolder();
  };

  const handleClickArchive = () => {
    Object.keys(selectedCard).length > 0 && toggleArchiveVideos();
    Object.keys(selectedFolder).length > 0 && toggleArchiveFolders();
  };

  const handleUnarchive = () => {
    Object.keys(selectedCard).length > 0 && toggleUnarchive();
    Object.keys(selectedFolder).length > 0 && toggleUnarchiveFolders();
  };

  const handleOpenShareFolderModal = () => {
    const tempSelectedFolder = Object.values(selectedFolder)[0];
    shareFolder(tempSelectedFolder);
  };

  const handleRemoveFromReviewClick = () => {
    toggleRemoveFromReviewVideo();
  };

  return (
    <div className={styles.rightSidebarContainer}>
      <div className={styles.wrapper}>
        <RightSidebarHeader selectedVideos={Object.keys(selectedCard).length} selectedFolders={Object.keys(selectedFolder).length} />
        <SidebarButton
          visible={!isArchive && Object.keys(selectedFolder).length === 0 && isUserHasAdvancedEditingRights}
          label={t('general.video_action_button.move')}
          icon={'move'}
          onClick={handleMoveClick}
        />
        {Object.keys(selectedCard).length === 1 && Object.keys(selectedFolder).length === 0 && !isArchive && (
          <>
            <SidebarButton
              visible={!isProcessing && (!isApprovalFlowEnabled || (!isInReviewStatus && !isApprovedStatus)) && isUserHasEditingRights}
              disabled={isDisabled}
              label={t('general.video_action_button.rename')}
              icon="rename"
              onClick={handleRenameClick}
            />
            <SidebarButton
              visible={!isProcessing && (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && !isDraftVideos}
              disabled={isDisabled}
              label={t('general.video_action_button.copy_link')}
              icon="copy_link"
              onClick={handleCopyLink}
            />
            <SidebarButton
              visible={!isProcessing && (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && !isDraftVideos}
              disabled={isDisabled}
              label={t('general.video_action_button.share')}
              icon="share"
              onClick={handleShareClick}
            />
            <SidebarButton
              visible={!isProcessing && isApprovalFlowEnabled && isInReviewStatus && isUserHasEditingRights}
              disabled={isDisabled}
              label={t('general.video_action_button.remove_from_review')}
              icon="revoke"
              onClick={handleRemoveFromReviewClick}
            />
            <SidebarButton
              visible={!isProcessing && isUserHasEditingRights}
              disabled={isDisabled}
              label={t('general.video_action_button.duplicate')}
              icon="duplicate"
              onClick={handleDuplicateClick}
            />
            <SidebarButton
              visible={!isProcessing && (!isApprovalFlowEnabled || isApprovedStatus || isPreExistedStatus) && !isDraftVideos}
              label={t('general.video_action_button.download')}
              icon="download"
              onClick={handleDownloadClick}
              hoverText={t('my_video.download.not_supported_notification')}
              hoverOnDisabled
            />
          </>
        )}

        {Object.keys(selectedFolder).length === 1 &&
          Object.keys(selectedCard).length === 0 &&
          !isArchive &&
          (isFoldersOwner || isFoldersAdmin) && (
            <>
              <SidebarButton
                disabled={isDisabled}
                label={t('general.folder_action_button.share_folder')}
                icon="shareFolder"
                onClick={handleOpenShareFolderModal}
              />
              <SidebarButton
                disabled={isDisabled}
                label={t('general.video_action_button.rename')}
                icon="rename"
                onClick={handleRenameFolderClick}
              />
            </>
          )}

        {!isArchive && (
          <>
            <SidebarButton
              visible={Object.keys(selectedFolder).length === 0 && Object.keys(selectedCard).length !== 0 && isVideosOwner}
              label={t('general.video_action_button.archive')}
              icon="archive"
              onClick={handleClickArchive}
            />
            <SidebarButton
              visible={
                Object.keys(selectedCard).length === 0 && Object.keys(selectedFolder).length !== 0 && isFoldersOwner && !isTeamFolder
              }
              label={t('general.video_action_button.archive')}
              icon="archive"
              onClick={handleClickArchive}
            />
            <SidebarButton
              visible={
                Object.keys(selectedCard).length !== 0 && isVideosOwner && Object.keys(selectedFolder).length !== 0 && isFoldersOwner
              }
              label={t('general.video_action_button.archive')}
              icon="archive"
              onClick={handleClickArchive}
            />
          </>
        )}
        <SidebarButton
          visible={isArchive}
          label={t('general.video_action_button.unarchive')}
          icon="unarchive"
          onClick={handleUnarchive}
        />
        <SidebarButton
          visible={
            Object.keys(selectedFolder).length === 0 && Object.keys(selectedCard).length !== 0 && isUserHasAdvancedEditingRights
          }
          label={t('general.video_action_button.delete')}
          icon="bin"
          onClick={handleDeleteClick}
        />
        {/* The delete action is available to all users, but if the user is not the owner of the folder, then the delete action will actually delete him from the shared folder, but not the folder itself */}
        <SidebarButton
          visible={Object.keys(selectedCard).length === 0 && Object.keys(selectedFolder).length !== 0}
          label={t('general.video_action_button.delete')}
          icon="bin"
          onClick={handleDeleteClick}
        />
        <SidebarButton
          visible={Object.keys(selectedCard).length !== 0 && isUserHasEditingRights && Object.keys(selectedFolder).length !== 0}
          label={t('general.video_action_button.delete')}
          icon="bin"
          onClick={handleDeleteClick}
        />
        {loading && folderId && (
          <div className={styles.spinnerContainer}>
            <LoadingSpinner theme="light" />
          </div>
        )}
      </div>
    </div>
  );
};

export default RightSidebarMenu;
